import { useEffect, useState } from 'react';
import { sleep } from './core';

/**
 * This was lifted from 'React: Creating a Custom Hook for Pagination'
 * https://dev.to/admantium/react-creating-a-custom-hook-for-pagination-jni
 */
export const useStaticPagination = (data, itemsPerPage) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [currentData, setCurrentData] = useState([]);
  const [pageStart, setPageStart] = useState(0);
  const [pageEnd, setPageEnd] = useState(0);
  const totalItems = data.length;
  const maxPage = Math.ceil(totalItems / itemsPerPage);

  const updateData = () => {
    const begin = (currentPage - 1) * itemsPerPage;
    const end = begin + itemsPerPage;
    const page = data.slice(begin, end);
    setCurrentData(page);
    setPageEnd(begin + page.length);
    setPageStart(page.length ? begin + 1 : begin);
  };

  const next = () => {
    setCurrentPage(currentPage => Math.min(currentPage + 1, maxPage));
    updateData();
  };

  const prev = () => {
    setCurrentPage(currentPage => Math.max(currentPage - 1, 1));
    updateData();
  };

  const jump = (page) => {
    const pageNumber = Math.max(1, page);
    setCurrentPage(() => Math.min(pageNumber, maxPage));
    updateData();
  };

  return {
    next,
    prev,
    jump,
    currentData,
    currentPage,
    totalItems,
    pageStart,
    pageEnd,
    maxPage,
  };
};

export const useDynamicPagination = ({
  pageSize = 10,
  getCount = () => 0,
  getData = () => [],
  refreshed,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [currentData, setCurrentData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [pageStart, setPageStart] = useState(0);
  const [pageEnd, setPageEnd] = useState(0);
  const [maxPage, setMaxPage] = useState(1);
  const [pageLoading, setPageLoading] = useState(false);
  const [updated, setUpdated] = useState(undefined);

  const updateCount = async () => {
    const count = await getCount();
    setTotalItems(count);
    setMaxPage(Math.ceil(count / pageSize));
  };

  const loadPage = async (page) => {
    try {
      setCurrentPage(page);
      setPageLoading(true);
      const offset = (page - 1) * pageSize;
      // fetch data in background
      const counter = updateCount();
      const loadData = getData({ offset, limit: pageSize });
      // update page range while loading
      setPageStart(offset + 1);
      setPageEnd(offset + 1);
      // update range range
      const data = await loadData;
      setPageEnd(offset + data.length);
      setPageStart(data.length ? offset + 1 : offset);
      setUpdated(Date.now());
      setCurrentData(data);
      await counter;
    } finally {
      // allow pendings ticks to complete before lifting loading
      await sleep(1);
      setPageLoading(false);
    }
  };

  useEffect(async () => {
    await loadPage(currentPage);
  }, [currentPage, pageSize, refreshed]);

  const jump = async (page) => {
    const pageNumber = Math.min(Math.max(1, page), maxPage);
    await loadPage(pageNumber);
  };
  const next = () => jump(currentPage + 1);
  const prev = () => jump(currentPage - 1);

  return {
    next,
    prev,
    jump,
    currentData,
    currentPage,
    totalItems,
    pageStart,
    pageEnd,
    maxPage,
    updated,
    pageLoading,
  };
};

export default useStaticPagination;
